import {
  Alert,
  Box,
  Button,
  Chip,
  ClickAwayListener,
  FormControl,
  Grid,
  Grow,
  InputLabel,
  MenuItem,
  Paper,
  Popper,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { CarrierType, CarrierVisitDirection, CompanyType } from '@planning/app/api'
import { LoaderButton } from '@planning/components/LoaderButton'
import { CompanyAutocomplete } from '@planning/components/autocompletes/company-autocomplete/CompanyAutocomplete'
import { RailVisitAutoComplete } from '@planning/components/visit/RailVisitAutoComplete'
import { VesselVisitAutoComplete } from '@planning/components/visit/VesselVisitAutoComplete'
import { csvFieldSchemaMap } from '@planning/config/csvHeaderConfig'
import { OrderListUploadViewStoreV2 } from '@planning/pages/Order/stores/OrderListUploadViewStoreV2'
import { OrderListUploadDragAndDropZone } from '@planning/pages/VesselVisit/Components/Upload/OrderListUploadDragAndDropZone'
import { useTranslate } from '@tolgee/react'
import { observer } from 'mobx-react-lite'
import {
  exportToCsv,
  IDataExportConfig,
} from 'modules/ui/src/theme/components/table/utils/xlsxUtils'
import { FC, useRef, useState } from 'react'
import { Controller } from 'react-hook-form'
import { VesselVisitMismatchDialog } from './VesselVisitMismatchDialog'
import { getTemplateOptionalFieldSchema } from './utils'

interface IProps {
  store: OrderListUploadViewStoreV2
  enableVisitSelectField: boolean
  visitType?: CarrierType
  isRestow?: boolean
  register: any
  errors: any
  setValue: any
  control: any
}

export const OrderListUploadDocumentsContent: FC<IProps> = observer(
  ({ store, errors, control, enableVisitSelectField, visitType, isRestow }) => {
    const { t } = useTranslate()

    const { uploadFailedReason } = store.parsingStore

    const { vesselVisitItemStore, railVisitItemStore } = usePlanningStore()

    const vesselVisit = vesselVisitItemStore.elements[store.vesselVisitId ?? -1]
    const railVisit = railVisitItemStore.elements[store.railVisitId ?? -1]

    const [carrierVisitOption, setCarrierVisitOption] = useState('Train')

    const anchorRef = useRef(null)

    const handleDownloadTemplate = () => {
      if (isRestow) {
        const dummyRow = [{ containerNumber: '', position: '' }]
        const config: IDataExportConfig<{ containerNumber: string; position: string }> = {
          containerNumber: { header: 'containerNumber', type: 'string' },
          position: { header: 'position', type: 'string' },
        }
        exportToCsv('Restow upload template', dummyRow, config)
      } else {
        if (store.isGeneralCargoVisit) {
          const baseHeaders = [
            'Commodity Catalogue',
            'Commodity Code',
            'Reference Number',
            'Lot Number',
            'Planned Quantity',
            'Total Gross Weight',
            'Weight Unit',
            'IMO Classes',
            'IDs',
          ]

          const headers =
            visitType === CarrierType.Train
              ? [...baseHeaders, 'Rail Track', 'Rail Car Number']
              : baseHeaders

          const dummyRow: Record<string, any> = {}
          const config: IDataExportConfig<any> = {}

          headers.forEach((header, index) => {
            const key = `field_${index}`
            dummyRow[key] = ''
            config[key] = {
              header,
              type:
                header === 'Planned Quantity' || header === 'Total Gross Weight'
                  ? 'number'
                  : 'string',
            }
          })

          exportToCsv('General Cargo Order Upload Template', [dummyRow], config)
          return
        }

        const carrierType = store.vesselVisitId ? CarrierType.Vessel : CarrierType.Train
        const optionalSchema = getTemplateOptionalFieldSchema(carrierType, store.direction)
        const schema = csvFieldSchemaMap.get(carrierType)

        if (!schema) return

        const combinedSchema = [...schema, ...(optionalSchema || [])]

        const dummyRow: Record<string, any> = {}
        const config: IDataExportConfig<any> = {}

        combinedSchema.forEach(field => {
          dummyRow[field.inputName] = ''
          config[field.inputName] = {
            header: field.name,
            type:
              field.comparisonType === 'boolean'
                ? 'boolean'
                : field.comparisonType === 'number'
                  ? 'number'
                  : 'string', // Default to string for custom/string/stringArray/none
          }
        })

        const title = store.vesselVisitId ? 'Order Upload Template' : 'Rail Order Upload Template'

        exportToCsv(title, [dummyRow], config)
      }
    }

    const OrderGroupChip: FC<{ group: string }> = observer(({ group }) => {
      return (
        <Chip
          label={group}
          color={group === store.orderGroupStore.selectedGroup ? 'primary' : 'secondary'}
          size='small'
          sx={{ mr: '.5rem' }}
          onClick={() => store.orderGroupStore.setSelectedGroup(group)}
        />
      )
    })

    const AddOrderGroupChip: FC = observer(() => (
      <>
        <Chip
          sx={{ marginLeft: '1rem' }}
          label={`+ ${t('addGroup', 'Add Group')}`}
          ref={anchorRef}
          color='primary'
          variant='outlined'
          size='small'
          data-cy='upload-documents-add-group-btn'
          onClick={() => store.orderGroupStore.setAddMode(!store.orderGroupStore.isAddMode)}
        />

        <Popper
          open={store.orderGroupStore.isAddMode}
          anchorEl={anchorRef.current}
          role={undefined}
          placement='right'
          transition
          disablePortal
          sx={{
            zIndex: 10000,
            ml: '1rem !important',
          }}
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement === 'bottom-start' ? 'left bottom' : 'right top',
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={() => store.orderGroupStore.setAddMode(false)}>
                  <>
                    <TextField
                      data-cy='upload-documents-add-group-textfield'
                      size='small'
                      onChange={e => store.orderGroupStore.setCurrentGroup(e.target.value)}
                    />
                    <Button
                      data-cy='upload-documents-add-group-save-btn'
                      onClick={() => store.orderGroupStore.addGroup()}
                    >
                      OK
                    </Button>
                  </>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </>
    ))

    return (
      <Box sx={{ padding: '1rem' }}>
        <Typography variant='h4'>{t('fileDetails', 'File details')}</Typography>
        <VesselVisitMismatchDialog store={store} />
        <Box sx={{ mt: '1rem' }}>
          <Grid container spacing={2}>
            {!isRestow && !store.isGeneralCargoVisit && (
              <>
                <Grid mb={'.5rem'} size={12}>
                  <Box display={'flex'}>
                    <Typography variant='subtitle2'>{t('orderGroups', 'Order Groups')}</Typography>
                    <Controller
                      control={control}
                      name='orderGroup'
                      // rules={{ validate: () => store.orderGroupStore.groups.length >= 1 }}
                      render={() => <AddOrderGroupChip />}
                    />
                  </Box>

                  {errors.orderGroup && errors.orderGroup.type === 'validate' && (
                    <Typography
                      variant='caption'
                      display='block'
                      sx={{ color: '#FF4842', mt: '.5rem' }}
                    >
                      {t('selectAtLeastOneGroup', 'Select at least one group.')}
                    </Typography>
                  )}
                </Grid>

                {store.orderGroupStore.groups.length > 0 ? (
                  <Grid mb={'1rem'} size={12}>
                    {store.orderGroupStore.groups.map(group => (
                      <OrderGroupChip key={`order-group-chip-${group}-$`} group={group} />
                    ))}
                  </Grid>
                ) : (
                  ''
                )}
              </>
            )}

            <Grid size={12}>
              {visitType === CarrierType.Train
                ? railVisit &&
                  !enableVisitSelectField && (
                    <RailVisitAutoComplete
                      selectedRailVisit={railVisit.data}
                      disabled={!enableVisitSelectField}
                      label={`${t('railVisit', 'Rail visit')} *`}
                    />
                  )
                : vesselVisit &&
                  !enableVisitSelectField && (
                    <VesselVisitAutoComplete
                      selectedVesselVisit={vesselVisit.data}
                      disabled={!enableVisitSelectField}
                      label={`${t('vesselVisit', 'Vessel visit')} *`}
                    />
                  )}
            </Grid>

            {enableVisitSelectField && (
              <>
                <Grid size={4}>
                  <Controller
                    control={control}
                    name={`carrierVisit`}
                    render={({ field: { onChange } }) => (
                      <FormControl fullWidth error={!!errors.inboundCarrierType}>
                        <InputLabel>{t('carrierVisit', 'Carrier Visit')}</InputLabel>
                        <Select
                          defaultValue={carrierVisitOption}
                          value={carrierVisitOption}
                          label={t('carrierVisit', 'Carrier Visit')}
                          onChange={(e: any) => {
                            onChange(e.target.value)
                            setCarrierVisitOption(e.target.value)
                          }}
                        >
                          <MenuItem value='Train'>{t('train', 'Train')}</MenuItem>
                          <MenuItem value='Vessel'>{t('vessel', 'Vessel')}</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid size={8}>
                  <>
                    {carrierVisitOption === 'Train' ? (
                      <RailVisitAutoComplete
                        label={`${t('railVisit', 'Rail visit')} *`}
                        handleOnChange={async visit => {
                          await store.setRailVisitId(visit?.id)
                        }}
                        error={!store.setRailVisitId}
                        helperText={
                          !store.setRailVisitId ? t('fieldIsRequired', 'Field is required.') : ''
                        }
                      />
                    ) : (
                      <VesselVisitAutoComplete
                        label={`${t('vesselVisit', 'Vessel visit')} *`}
                        handleOnChange={async visit => {
                          await store.setVesselVisitId(visit?.id)
                        }}
                        error={!store.setVesselVisitId}
                        helperText={
                          !store.setVesselVisitId ? t('fieldIsRequired', 'Field is required.') : ''
                        }
                      />
                    )}
                  </>
                </Grid>
              </>
            )}

            <Grid container size={12} spacing={2}>
              <Grid size={6}>
                <Controller
                  control={control}
                  name='customer'
                  rules={{ required: true }}
                  render={({ field: { onChange } }) => (
                    <CompanyAutocomplete
                      selectedCustomer={store.customer}
                      handleOnChange={customer => {
                        onChange(customer)
                        store.setCustomer(customer ?? undefined)
                      }}
                      error={!!errors.customer}
                      helperText={errors.customer ? t('fieldIsRequired', 'Field is required.') : ''}
                      filterByTypes={
                        store.isGeneralCargoVisit ? undefined : [CompanyType.ShippingLine]
                      }
                    />
                  )}
                />
              </Grid>
              <Grid size={6}>
                <Controller
                  control={control}
                  name={`direction`}
                  render={({ field: { onChange } }) => (
                    <FormControl fullWidth>
                      <InputLabel color='secondary'>{t('Operation', 'Operation *')}</InputLabel>
                      <Select
                        label={`${t('direction', 'Direction')} *`}
                        disabled={!enableVisitSelectField}
                        fullWidth
                        value={isRestow ? 'Restow' : store.direction}
                        onChange={e => {
                          onChange(e)
                          store.setDirection(e.target.value as CarrierVisitDirection)
                        }}
                      >
                        <MenuItem value={CarrierVisitDirection.Inbound}>
                          {t('discharge', 'Discharge')}
                        </MenuItem>
                        <MenuItem value={CarrierVisitDirection.Outbound}>
                          {t('load', 'Load')}
                        </MenuItem>
                        {isRestow && <MenuItem value={'Restow'}>{t('restow', 'Restow')}</MenuItem>}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>

            <Grid size={12}>
              <OrderListUploadDragAndDropZone store={store} />
            </Grid>

            <Box display='flex' alignItems='center' justifyContent='center' width='100%'>
              <LoaderButton
                onClick={handleDownloadTemplate}
                color='inherit'
                variant='outlined'
                data-cy='download-template-btn'
              >
                {t('downloadTemplate', 'Download template')}
              </LoaderButton>
            </Box>

            {uploadFailedReason && (
              <Grid>
                <Alert severity='error'>
                  <Typography variant='body2' color='error'>
                    {uploadFailedReason}
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>
    )
  },
)
